import { Link } from "react-router-dom";
import { User, Briefcase, FileCheck, Landmark, Check, ArrowRight } from "lucide-react";
import PageHero from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import CTASection from "../components/CTASection";
import { IMAGES } from "../lib/siteConfig";

const SERVICES = [
  {
    id: "personal",
    icon: User,
    title: "Personal Loan Assistance",
    lead: "Structured guidance for salaried and self-employed individuals exploring personal loan options.",
    points: [
      "Eligibility guidance based on your profile",
      "Documentation checklist and preparation support",
      "Comparison of suitable options from banks and NBFCs",
      "Coordination through the application process",
    ],
  },
  {
    id: "business",
    icon: Briefcase,
    title: "Business Loan Assistance",
    lead: "Support for MSMEs, traders and growing businesses seeking working capital or expansion funding.",
    points: [
      "Working capital and MSME loan guidance",
      "Business financial documentation support",
      "Matching with institutions suited to your business profile",
      "Application and follow-up coordination",
    ],
  },
  {
    id: "application",
    icon: FileCheck,
    title: "Loan Application Support",
    lead: "End-to-end assistance that keeps your application complete, accurate and moving forward.",
    points: [
      "Application form assistance and review",
      "Document collection and checklist coordination",
      "Follow-ups with the institution's processing desk",
      "Status updates and clear communication",
    ],
  },
  {
    id: "connect",
    icon: Landmark,
    title: "Bank & Financial Institution Connect",
    lead: "We connect eligible customers with banks and financial institutions offering suitable loan products.",
    points: [
      "Profile-based matching with relevant lenders",
      "Coordination with bank and NBFC desks",
      "Guidance on institution-specific requirements",
      "Support until your application is submitted",
    ],
  },
];

export default function Services() {
  return (
    <main>
      <PageHero
        testid="services-hero"
        eyebrow="Our Services"
        titleLines={["Assistance That Moves", "Your Application Forward"]}
        description="From personal loans to business funding, we support you at every stage — guidance, documentation, coordination and connection with the right financial institution."
      />

      <section className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto pb-8" data-testid="services-list">
        <div className="space-y-6">
          {SERVICES.map((s, i) => (
            <Reveal key={s.id} delay={0.05}>
              <div
                data-testid={`service-detail-${s.id}`}
                className="card-gold p-8 sm:p-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-start hover:!transform-none"
              >
                <div className="lg:col-span-1">
                  <div className="w-12 h-12 rounded-xl border border-[rgba(212,175,55,0.3)] bg-[#0A0A0D] flex items-center justify-center">
                    <s.icon size={22} className="text-[#D4AF37]" strokeWidth={1.5} />
                  </div>
                </div>
                <div className="lg:col-span-5">
                  <p className="font-mono-brand text-xs text-[#D4AF37] tracking-[0.25em] mb-3">SERVICE 0{i + 1}</p>
                  <h2 className="font-display text-xl sm:text-2xl font-semibold text-white mb-3">{s.title}</h2>
                  <p className="text-sm sm:text-base text-[#A1A1AA] leading-relaxed">{s.lead}</p>
                </div>
                <div className="lg:col-span-6">
                  <ul className="space-y-3">
                    {s.points.map((p) => (
                      <li key={p} className="flex items-start gap-3 text-sm text-[#A1A1AA]">
                        <Check size={15} className="text-[#D4AF37] mt-0.5 shrink-0" />
                        {p}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/contact"
                    data-testid={`service-cta-${s.id}`}
                    className="inline-flex items-center gap-2 text-sm text-[#E6CA65] hover:text-[#F3E5AB] mt-6 transition-colors"
                  >
                    Enquire about this service <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-16" data-testid="services-image-strip">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {[IMAGES.agreement, IMAGES.handshake].map((src, i) => (
            <Reveal key={src} delay={i * 0.1}>
              <div className="rounded-2xl overflow-hidden border border-[rgba(212,175,55,0.2)] h-56 sm:h-72">
                <img src={src} alt="" className="w-full h-full object-cover opacity-70 hover:opacity-100 hover:scale-105 transition-all duration-700" />
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}>
          <p className="text-center text-xs text-[#71717A] mt-8 max-w-2xl mx-auto leading-relaxed">
            CREDNOVA SERVICES PRIVATE LIMITED provides assistance and facilitation services only. We do not lend
            directly, and loan approval is subject to the eligibility criteria and policies of the respective
            bank or financial institution.
          </p>
        </Reveal>
      </section>

      <CTASection />
    </main>
  );
}
